/**
 * ZAPAMIĘTANY ZESTAW KOLUMN RAPORTU — z ODCISKIEM DOMYŚLNYCH, po których go zapamiętano.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z BŁĘDU ZGŁOSZONEGO PRZEZ UŻYTKOWNIKA (31.08.2026): „miałeś z tego menu przenieść
 *  pozycje 3, 7 i 8 jako opcjonalne, a cały czas są wybrane domyślnie".
 *
 *  Zmiana zestawu domyślnego weszła po stronie SERWERA i była poprawna — ale okno startowało
 *  z zestawu ZAPAMIĘTANEGO W PRZEGLĄDARCE, zapisanego jeszcze pod starymi domyślnymi. Pamięć
 *  cicho przebijała nową decyzję i nie było tego po czym poznać: okno wyglądało dokładnie tak,
 *  jak przed poprawką, a jedynym wyjściem był przycisk „przywróć domyślne", o którym trzeba
 *  wiedzieć.
 *
 *  ⚠️⚠️ PAMIĘĆ MA PAMIĘTAĆ CZYJŚ WYBÓR, A NIE POPRZEDNIE DOMYŚLNE. Dlatego razem z kluczami
 *  zapisujemy ODCISK zestawu domyślnego z chwili zapamiętania: gdy serwer zmieni domyślne, pamięć
 *  jest UNIEWAŻNIANA i okno startuje z nowych. Inaczej każda nasza zmiana domyślnych dotyczyłaby
 *  wyłącznie osób, które tego okna jeszcze nie otwierały.
 *
 *  ⚠️ ŚWIADOMY KOSZT: ktoś, kto sam wyrzucił z pliku „Wartość", straci tę zmianę przy najbliższej
 *  zmianie domyślnych. To jest tańszy błąd niż odwrotny — jego widać od razu i naprawia się dwoma
 *  kliknięciami, a niewidoczne przebicie decyzji wraca do nas jako „przecież mówiłem, żeby to
 *  wywalić" i kosztuje zaufanie do całego ekranu.
 *
 *  ⚠️ STARY FORMAT (goła tablica kluczy) UZNAJEMY ZA NIEWAŻNY. Nie da się z niego odczytać, pod
 *  jakimi domyślnymi powstał, a wszystkie takie zapisy są SPRZED tej zmiany — czyli dokładnie te,
 *  które trzymają zestaw sprzed poprawki.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Klucz w pamięci przeglądarki — osobny per raport, bo kolumny wydań nie istnieją w zatowarowaniu. */
export const kluczPamieci = (raport: string): string => `vendi.raport.kolumny.${raport}`;

/**
 * ODCISK ZESTAWU DOMYŚLNEGO.
 *
 * ⚠️ KOLEJNOŚĆ MA ZNACZENIE, bo domyślne to lista uporządkowana: przestawienie kolumn w katalogu
 * jest zmianą pliku, który ktoś dostaje, więc też ma unieważniać pamięć.
 */
export const odciskDomyslnych = (domyslne: readonly string[]): string => domyslne.join('|');

type Zapis = { v: 2; klucze: string[]; odcisk: string };

const czyZapis = (x: unknown): x is Zapis => {
  if (typeof x !== 'object' || x === null) return false;
  const z = x as Partial<Zapis>;
  return (
    z.v === 2 &&
    Array.isArray(z.klucze) &&
    z.klucze.every((k) => typeof k === 'string') &&
    typeof z.odcisk === 'string'
  );
};

/**
 * Odczyt pamięci — `null`, gdy jej nie ma, jest nieczytelna ALBO powstała pod innymi domyślnymi.
 *
 * `magazyn` wstrzykiwany, żeby dało się to sprawdzić testem bez przeglądarki.
 */
export function odczytajZestaw(
  raport: string,
  domyslne: readonly string[],
  magazyn: Pick<Storage, 'getItem'> | null = bezpiecznyMagazyn(),
): string[] | null {
  if (magazyn === null) return null;
  try {
    const surowe = magazyn.getItem(kluczPamieci(raport));
    if (surowe === null) return null;
    const dane: unknown = JSON.parse(surowe);
    if (!czyZapis(dane)) return null;
    /** ⚠️ TU UNIEWAŻNIAMY: inne domyślne niż w chwili zapisu znaczą, że decyzja jest nowsza od pamięci. */
    if (dane.odcisk !== odciskDomyslnych(domyslne)) return null;
    return dane.klucze;
  } catch {
    /** Prywatny tryb, zapchany magazyn, uszkodzony JSON — pamięć jest wygodą, nie warunkiem. */
    return null;
  }
}

export function zapamietajZestaw(
  raport: string,
  klucze: readonly string[],
  domyslne: readonly string[],
  magazyn: Pick<Storage, 'setItem'> | null = bezpiecznyMagazyn(),
): void {
  if (magazyn === null) return;
  try {
    const zapis: Zapis = { v: 2, klucze: [...klucze], odcisk: odciskDomyslnych(domyslne) };
    magazyn.setItem(kluczPamieci(raport), JSON.stringify(zapis));
  } catch {
    /** Zapamiętanie jest wygodą, nie warunkiem — brak miejsca nie ma prawa przerwać pobierania. */
  }
}

/** `localStorage` bywa niedostępny (prywatny tryb, polityka przeglądarki) i sam dostęp rzuca. */
function bezpiecznyMagazyn(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
